import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaPlus,
  FaPhone,
} from "react-icons/fa";
import { Button } from "../../components/ui/button";
import Common from "../../helper/common";
import { ClubConfigTypeEnum, respEnum } from "../../constants/appConstant";
import Card from "../../components/ui/card-snippet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { PhoneInputField } from "../../components/formik-components";
import { Formik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import { AppSelect } from "../../components/appComponents";

// A more comprehensive country phone rules database
const countryPhoneRules = {
  // Format: country code -> rules object
  1: {
    // USA/Canada
    format: "xxx-xxx-xxxx",
    validLengths: [10], // Valid number lengths after removing formatting
    hasLeadingZero: false, // Whether the number can start with 0
    mobilePrefix: null, // Special prefix for mobile numbers if any
    description: "US/Canada",
  },
  44: {
    // UK
    format: "xxxx xxx xxxx",
    validLengths: [10, 11],
    hasLeadingZero: true, // UK mobile numbers start with '07'
    mobilePrefix: "7",
    description: "United Kingdom",
  },
  91: {
    // India
    format: "xxxxx xxxxx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: null,
    description: "India",
  },
  92: {
    // Pakistan
    format: "xxx xxxxxxx",
    validLengths: [10],
    hasLeadingZero: true, // Pakistan mobile numbers can start with '0'
    mobilePrefix: "3", // Most Pakistan mobile numbers start with 3
    description: "Pakistan",
  },
  86: {
    // China
    format: "xxx xxxx xxxx",
    validLengths: [11],
    hasLeadingZero: false,
    mobilePrefix: "1",
    description: "China",
  },
  33: {
    // France
    format: "x xx xx xx xx",
    validLengths: [9],
    hasLeadingZero: true,
    mobilePrefix: "6",
    description: "France",
  },
  49: {
    // Germany
    format: "xxx xxxxxxxx",
    validLengths: [10, 11],
    hasLeadingZero: true,
    mobilePrefix: "1",
    description: "Germany",
  },
  81: {
    // Japan
    format: "xx xxxx xxxx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: "80",
    description: "Japan",
  },
  82: {
    // South Korea
    format: "xx xxxx xxxx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: "1",
    description: "South Korea",
  },
  7: {
    // Russia
    format: "xxx xxx xx xx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: "9",
    description: "Russia",
  },
  55: {
    // Brazil
    format: "xx xxxxx xxxx",
    validLengths: [10, 11],
    hasLeadingZero: false,
    mobilePrefix: null,
    description: "Brazil",
  },
  971: {
    // UAE
    format: "xx xxx xxxx",
    validLengths: [9],
    hasLeadingZero: false,
    mobilePrefix: "5",
    description: "UAE",
  },
  966: {
    // Saudi Arabia
    format: "xx xxx xxxx",
    validLengths: [9],
    hasLeadingZero: false,
    mobilePrefix: "5",
    description: "Saudi Arabia",
  },
  234: {
    // Nigeria
    format: "xx xxxx xxxx",
    validLengths: [10],
    hasLeadingZero: true,
    mobilePrefix: "7",
    description: "Nigeria",
  },
  27: {
    // South Africa
    format: "xx xxx xxxx",
    validLengths: [9],
    hasLeadingZero: true,
    mobilePrefix: "6",
    description: "South Africa",
  },
  // Add more countries as needed
};

// Default rules for countries not specifically listed
const defaultPhoneRules = {
  format: "xxxxxxxxxx",
  validLengths: [7, 8, 9, 10, 11, 12, 13, 14, 15],
  hasLeadingZero: true,
  mobilePrefix: null,
  description: "International",
};

// Function to detect country code from a phone number
const detectCountryCode = (phone) => {
  if (!phone || typeof phone !== "string") return null;

  // Remove any non-digit characters except the leading '+' sign
  const cleanPhone = phone.replace(/[^\d+]/g, "");

  // Must start with a '+' sign
  if (!cleanPhone.startsWith("+")) return null;

  // Remove the '+' sign and check the remainder
  const numberWithoutPlus = cleanPhone.substring(1);
  if (!numberWithoutPlus) return null;

  // Try to match country codes from longest to shortest
  // This handles cases where one country code is a prefix of another
  // (e.g., '1' for US/Canada vs '1246' for Barbados)
  const sortedCodes = Object.keys(countryPhoneRules).sort(
    (a, b) => b.length - a.length
  );

  for (const code of sortedCodes) {
    if (numberWithoutPlus.startsWith(code)) {
      return {
        code,
        rules: countryPhoneRules[code],
        number: numberWithoutPlus.substring(code.length),
      };
    }
  }

  // If we couldn't find a specific country code, use default rules
  // Just use the first 1-3 digits as a "generic" country code
  const genericCode = numberWithoutPlus.substring(
    0,
    Math.min(3, numberWithoutPlus.length)
  );
  return {
    code: genericCode,
    rules: defaultPhoneRules,
    number: numberWithoutPlus.substring(genericCode.length),
  };
};

// Function to normalize phone number format
const normalizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string") return "";

  // Clean the phone number to just digits and plus sign
  return phone.replace(/[^\d+]/g, "");
};

// Custom validation for phone numbers based on country code
const validatePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string") return false;

  // Normalize the phone number
  const normalizedPhone = normalizePhoneNumber(phone);
  if (!normalizedPhone.startsWith("+")) return false;

  // Detect country code and get rules
  const countryInfo = detectCountryCode(normalizedPhone);
  if (!countryInfo) return false;

  const { code, rules, number } = countryInfo;

  // Clean the number part (remove leading zero if needed)
  let cleanNumber = number;
  if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.substring(1);
  }

  // Check if the number length matches any of the valid lengths
  return rules.validLengths.includes(cleanNumber.length);
};

// Get error message for invalid phone numbers
const getErrorMessage = (phone) => {
  if (!phone) return "Phone number is required";
  if (typeof phone !== "string") return "Invalid phone number format";

  // Normalize the phone number
  const normalizedPhone = normalizePhoneNumber(phone);
  if (!normalizedPhone.startsWith("+"))
    return "Phone number must start with a country code (e.g., +1, +92)";

  // Detect country code and get rules
  const countryInfo = detectCountryCode(normalizedPhone);
  if (!countryInfo) return "Could not determine country code";

  const { code, rules, number } = countryInfo;

  // Clean the number part (remove leading zero if needed)
  let cleanNumber = number;
  if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.substring(1);
  }

  // Check if the length is valid
  if (!rules.validLengths.includes(cleanNumber.length)) {
    const lengthsText =
      rules.validLengths.length > 1
        ? `one of ${rules.validLengths.join(", ")}`
        : rules.validLengths[0];

    const leadingZeroText = rules.hasLeadingZero
      ? " (any leading zero will be removed)"
      : "";

    return `Invalid phone number length for ${rules.description}. Should be ${lengthsText} digits after country code${leadingZeroText}.`;
  }

  return "";
};

// Simple validation schema for Formik, we'll do detailed validation on save
const formikValidation = yup.object({
  phone: yup.string().required("Phone Number is required"),
});

let tempIdCounter = 1;
export default function NotificationConfigs() {
  const [configs, setConfigs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [groupedConfigs, setGroupedConfigs] = useState({});
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    getClubs();
  }, []);

  useEffect(() => {
    if (selectedClubId) {
      getClubNotificationConfigs(selectedClubId);
    }
  }, [selectedClubId]);

  useEffect(() => {
    if (configs.length > 0) {
      // Group configs by type
      const grouped = configs.reduce((acc, config) => {
        const typeKey = config.type;
        if (!acc[typeKey]) {
          acc[typeKey] = [];
        }
        acc[typeKey].push(config);
        return acc;
      }, {});

      setGroupedConfigs(grouped);
    } else {
      setGroupedConfigs({});
    }
  }, [configs]);

  const getClubs = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetClubsDDL"
      );
      if (response?.data) {
        setClubs([{ id: null, name: "Select Club" }, ...response.data]);
      } else {
        setClubs([{ id: null, name: "Select Club" }]);
        toast.error("Failed to load clubs data");
      }
    } catch (error) {
      console.error("Error fetching clubs:", error);
      toast.error("An error occurred while fetching clubs");
      setClubs([{ id: null, name: "Select Club" }]);
    }
  };

  const getClubNotificationConfigs = async (clubId) => {
    try {
      const response = await Common.ApiService.getInstance().request(
        `GetClubNotificationConfigs?clubId=${clubId}`
      );
      if (response?.data) {
        setConfigs(response.data);
      } else {
        setConfigs([]);
      }
    } catch (error) {
      console.error("Error fetching notification configs:", error);
      toast.error(
        "An error occurred while fetching notification configurations"
      );
      setConfigs([]);
    }
  };

  const handleClubChange = (val) => {
    setSelectedClubId(parseInt(val));
  };

  const handleAddConfig = (type) => {
    const newConfig = {
      id: 0, // Use 0 for new records instead of timestamp
      tempId: tempIdCounter++,
      clubId: selectedClubId,
      phone: "",
      phoneCode: "+92",
      phoneNum: "",
      isActive: true,
      type,
    };

    // Add this config to existing configs
    setConfigs([...configs, newConfig]);
  };

  // Check if a phone number already exists in the same group
  const isDuplicatePhone = (phone, type, configId) => {
    if (!phone) return false;

    // Normalize the phone number for comparison (remove spaces, dashes, etc.)
    const normalizedPhone = normalizePhoneNumber(phone);

    // Check if any other config in the same group has the same phone number
    return configs.some(
      (config) =>
        config.id !== configId && // Not the same config
        config.type === type && // Same group/type
        config.phone && // Has a phone number
        normalizePhoneNumber(config.phone) === normalizedPhone // Same phone after normalization
    );
  };

  const handleDeleteConfig = (configId, tempId) => {
    // Remove the validation error for this config
    const newErrors = { ...validationErrors };
    delete newErrors[configId];
    setValidationErrors(newErrors);

    // Remove the config
    // setConfigs(configs.filter((config) => config.id !== configId));
    setConfigs((prev) =>
      prev.filter(
        (c) =>
          !(
            (c.id === configId && configId !== 0) ||
            (c.tempId && c.tempId === tempId)
          )
      )
    );
  };

  const toggleActiveStatus = (configId, tempId) => {
    // setConfigs(
    //   configs.map((config) =>
    //     config.id === configId
    //       ? { ...config, isActive: !config.isActive }
    //       : config
    //   )
    // );
    setConfigs((prev) =>
      prev.map((config) => {
        if (
          (configId !== 0 && config.id === configId) ||
          (configId === 0 && config.tempId === tempId)
        ) {
          return { ...config, isActive: !config.isActive };
        }
        return config;
      })
    );
  };

  // Helper function to parse a phone number into country code and number
  const parsePhoneNumber = (phone) => {
    if (!phone || typeof phone !== "string")
      return { phoneCode: "+92", phoneNum: "" };

    const countryInfo = detectCountryCode(phone);
    if (!countryInfo) return { phoneCode: "+92", phoneNum: "" };

    return {
      phoneCode: `+${countryInfo.code}`,
      phoneNum: countryInfo.number,
    };
  };

  // Function to format phone number with country code when saving
  const formatPhoneNumber = (phoneCode, phoneNum) => {
    if (!phoneCode && !phoneNum) return "";

    // Normalize phone code to ensure it has a plus sign
    const normalizedCode = phoneCode.startsWith("+")
      ? phoneCode
      : `+${phoneCode}`;

    // Remove any non-digit characters from the phone number
    let cleanNumber = phoneNum.replace(/[^\d]/g, "");

    // Get the country code without the plus sign
    const codeWithoutPlus = normalizedCode.substring(1);

    // Check if the country has specific rules
    const rules = countryPhoneRules[codeWithoutPlus] || defaultPhoneRules;

    // Handle leading zeros based on country rules
    if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
      // Some countries (like Pakistan) might have optional leading zero
      // We'll remove it as the country code already identifies the country
      cleanNumber = cleanNumber.substring(1);
    }

    return `${normalizedCode}${cleanNumber}`;
  };

  const updatePhoneNumber = (configId, phone, phoneCode, phoneNum, tempId) => {
    // Ensure we have valid values
    const parsedPhone = phone || "";
    const parsedPhoneCode =
      phoneCode || parsePhoneNumber(parsedPhone).phoneCode;
    const parsedPhoneNum = phoneNum || parsePhoneNumber(parsedPhone).phoneNum;

    // Format the complete phone number
    const fullPhone = formatPhoneNumber(parsedPhoneCode, parsedPhoneNum);

    console.log("Updated phone number:", {
      parsedPhone,
      parsedPhoneCode,
      parsedPhoneNum,
      fullPhone,
    });

    // Clear any existing validation error for this config
    if (validationErrors[configId]) {
      const newErrors = { ...validationErrors };
      delete newErrors[configId];
      setValidationErrors(newErrors);
    }

    // Update only the specific config
    // setConfigs(
    //   configs.map((config) =>
    //     config.id === configId
    //       ? {
    //           ...config,
    //           phone: fullPhone,
    //           phoneCode: parsedPhoneCode,
    //           phoneNum: parsedPhoneNum,
    //         }
    //       : config
    //   )
    // );
    setConfigs((prev) =>
      prev.map((config) => {
        if (
          (configId !== 0 && config.id === configId) ||
          (configId === 0 && config.tempId === tempId)
        ) {
          return {
            ...config,
            phone: fullPhone,
            phoneCode: parsedPhoneCode,
            phoneNum: parsedPhoneNum,
          };
        }
        return config;
      })
    );
  };

  // Function to display validation issues on Save button click and in console for debugging
  const debugPhoneValidation = (phone) => {
    if (!phone || typeof phone !== "string") {
      console.log("Invalid phone - null, undefined or not a string:", phone);
      return;
    }

    console.group("Phone Validation Debug");
    console.log("Phone number:", phone);

    // Normalize the phone number
    const normalizedPhone = normalizePhoneNumber(phone);
    console.log("Normalized phone:", normalizedPhone);

    // Detect country code
    const countryInfo = detectCountryCode(normalizedPhone);
    if (!countryInfo) {
      console.log("Failed to detect country code");
      console.groupEnd();
      return;
    }

    const { code, rules, number } = countryInfo;
    console.log("Detected country code:", code);
    console.log("Country rules:", rules);
    console.log("Number portion:", number);

    // Clean the number part (remove leading zero if needed)
    let cleanNumber = number;
    if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
      cleanNumber = cleanNumber.substring(1);
      console.log("Removed leading zero, clean number:", cleanNumber);
    }

    // Check if the length is valid
    const isValidLength = rules.validLengths.includes(cleanNumber.length);
    console.log("Valid lengths:", rules.validLengths);
    console.log("Current length:", cleanNumber.length);
    console.log("Is valid length:", isValidLength);

    // Overall validation result
    const isValid = validatePhoneNumber(phone);
    console.log("Overall validation result:", isValid);

    if (!isValid) {
      console.log("Error message:", getErrorMessage(phone));
    }

    console.groupEnd();
  };

  // Validate all configs and return any errors
  const validateAllConfigs = () => {
    const errors = {};

    for (const config of configs) {
      if (!config.phone || typeof config.phone !== "string") {
        errors[config.id] = "Phone number is required";
        continue;
      }

      // Check for duplicate phone numbers in the same group
      if (isDuplicatePhone(config.phone, config.type, config.id)) {
        errors[config.id] = "Duplicate phone number in this group";
        continue;
      }

      // Log what we're validating
      console.log("Validating phone:", {
        id: config.id,
        phone: config.phone,
        isValid: validatePhoneNumber(config.phone),
        errorMessage: getErrorMessage(config.phone),
      });

      if (!validatePhoneNumber(config.phone)) {
        errors[config.id] = getErrorMessage(config.phone);
      }
    }

    console.log("Validation errors:", errors);
    return errors;
  };

  const saveConfigs = async () => {
    if (!selectedClubId) {
      toast.error("Please select a club");
      return;
    }

    if (configs.length === 0) {
      toast.error("No configuration to save");
      return;
    }

    // Debug validation for each phone number
    configs.forEach((config) => {
      debugPhoneValidation(config.phone);
    });

    // Validate all configs before saving
    const validationErrors = validateAllConfigs();
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors(validationErrors);

      // Show error for first invalid phone
      const firstErrorId = Object.keys(validationErrors)[0];
      const firstErrorMsg = validationErrors[firstErrorId];
      toast.error(`Invalid phone number: ${firstErrorMsg}`);
      return;
    }

    setSaveBtnDisabled(true);

    try {
      // Prepare data for API
      const data = configs.map((config) => ({
        id: config.id, // Keep id as is (0 for new configs)
        clubId: selectedClubId,
        type: config.type,
        phone: config.phone,
        isActive: config.isActive,
      }));

      const response = await Common.ApiService.getInstance().request(
        "AddUpdateClubNotificationConfigs",
        data,
        "POST"
      );

      if (response.status === respEnum.Success) {
        toast.success("Configurations saved successfully");
        // Clear dummy/temp configs before refetch
        setConfigs([]);
        // Refresh data from server to get actual IDs
        await getClubNotificationConfigs(selectedClubId);
        // Clear any validation errors
        setValidationErrors({});
      } else {
        toast.error(response.message || "Failed to save configurations");
      }
    } catch (error) {
      console.error("Error saving configurations:", error);
      toast.error("An error occurred while saving configurations");
    } finally {
      setSaveBtnDisabled(false);
    }
  };

  // Function to get config type name from enum
  const getConfigTypeName = (type) => {
    switch (type) {
      case ClubConfigTypeEnum.WhatsApp_Message_To_Owners:
        return "WhatsApp Message To Owners";
      case ClubConfigTypeEnum.Others:
        return "Others";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border shadow-sm overflow-hidden">
        <div className="bg-[#0C1B3A] p-4">
          <h2 className="text-xl font-semibold text-white">
            Club Notification Configs
          </h2>
        </div>

        <div className="p-6">
          <div className="max-w-md mb-6">
            <label className="block text-sm font-medium mb-2 text-[#0C1B3A]">
              Select Club
            </label>
            <AppSelect
              data={clubs}
              placeholder="Select Club"
              value={selectedClubId}
              onChange={handleClubChange}
            />
          </div>

          {selectedClubId && (
            <div className="space-y-6">
              {Object.keys(groupedConfigs).map((type) => {
                const configType = parseInt(type);
                return (
                  <div
                    key={`group-${configType}`}
                    className="border rounded-lg shadow-sm overflow-hidden"
                  >
                    <div className="bg-[#0C1B3A]/90 p-4 flex justify-between items-center">
                      <h3 className="text-lg font-medium text-white flex items-center">
                        <FaPhone className="mr-2" />
                        {getConfigTypeName(configType)}
                      </h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex items-center gap-2 text-white hover:bg-[#0C1B3A]/70"
                        onClick={() => handleAddConfig(configType)}
                      >
                        <FaPlus className="h-3 w-3" />
                        Add Phone
                      </Button>
                    </div>

                    <div className="overflow-hidden">
                      <Table>
                        <TableHeader className="bg-gray-50">
                          <TableRow>
                            <TableHead className="py-3 px-4 text-[#0C1B3A] font-medium w-full md:w-100">
                              Phone
                            </TableHead>
                            <TableHead className="w-24 text-center py-3 px-4 text-[#0C1B3A] font-medium">
                              Is Active
                            </TableHead>
                            <TableHead className="w-24 text-center py-3 px-4 text-[#0C1B3A] font-medium">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupedConfigs[type].map((config) => (
                            <TableRow
                              key={`config-${config.id}-${config.type}`}
                              className={`border-b hover:bg-gray-50 transition-colors ${
                                validationErrors[config.id] ? "bg-red-50" : ""
                              }`}
                            >
                              <TableCell className="py-3 px-4">
                                <div className="max-w-[240px]">
                                  <Formik
                                    initialValues={{
                                      phone: config.phone || "",
                                      phoneCode:
                                        config.phoneCode ||
                                        parsePhoneNumber(config.phone)
                                          .phoneCode,
                                      phoneNum:
                                        config.phoneNum ||
                                        parsePhoneNumber(config.phone).phoneNum,
                                    }}
                                    validationSchema={formikValidation}
                                    onSubmit={() => {}}
                                  >
                                    {(formikProps) => (
                                      <PhoneInputField
                                        formikProps={formikProps}
                                        onChange={(values) => {
                                          updatePhoneNumber(
                                            config.id,
                                            values.phone,
                                            values.phoneCode,
                                            values.phoneNum,
                                            config.tempId
                                          );
                                        }}
                                      />
                                    )}
                                  </Formik>
                                  {validationErrors[config.id] && (
                                    <div className="text-red-500 text-xs mt-1">
                                      {validationErrors[config.id]}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center py-3 px-4">
                                <button
                                  onClick={() =>
                                    toggleActiveStatus(config.id, config.tempId)
                                  }
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    config.isActive
                                      ? "bg-green-100 text-green-600"
                                      : "bg-red-100 text-red-600"
                                  }`}
                                >
                                  {config.isActive ? (
                                    <FaCheck className="w-4 h-4" />
                                  ) : (
                                    <FaTimes className="w-4 h-4" />
                                  )}
                                </button>
                              </TableCell>
                              <TableCell className="text-center py-3 px-4">
                                <button
                                  onClick={() =>
                                    handleDeleteConfig(config.id, config.tempId)
                                  }
                                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-100 text-red-500"
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })}

              {/* If no config exists for a particular type, display an option to add */}
              {Object.keys(ClubConfigTypeEnum).map((typeKey) => {
                const configType = ClubConfigTypeEnum[typeKey];
                if (!groupedConfigs[configType]) {
                  return (
                    <div
                      key={`empty-group-${configType}`}
                      className="border rounded-lg shadow-sm overflow-hidden"
                    >
                      <div className="bg-[#0C1B3A]/90 p-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-white flex items-center">
                          <FaPhone className="mr-2" />
                          {getConfigTypeName(configType)}
                        </h3>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex items-center gap-2 text-white hover:bg-[#0C1B3A]/70"
                          onClick={() => handleAddConfig(configType)}
                        >
                          <FaPlus className="h-3 w-3" />
                          Add Phone
                        </Button>
                      </div>
                      <div className="p-6 text-gray-500 italic text-center">
                        No configurations yet. Click "Add Phone" to create one.
                      </div>
                    </div>
                  );
                }
                return null;
              })}

              <div className="flex justify-end mt-8">
                <Button
                  onClick={saveConfigs}
                  disabled={saveBtnDisabled}
                  className="bg-[#0C1B3A] hover:bg-[#0C1B3A]/90 transition-colors"
                >
                  {saveBtnDisabled ? "Saving..." : "Save Configurations"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
